/**
 * Sign-in (`/signin`, web.md §4 "Additions"; api.md "Auth").
 *
 * The normal way in is one form: e-mail, password, Sign in (`platform/session.ts` `signInWithPassword`).
 * "Use a code instead" is the path for the first time here, a forgotten password, and while password
 * sign-in is paused after wrong passwords: e-mail → a six-digit code by e-mail → signed in. After a code
 * the screen offers to set a password (account without one) or choose a new one (forgot it) — both
 * skippable. Every step is a real <form> with autocomplete hints, so password managers save and fill it.
 *
 * Signing in stores the account, applies the entitlement and starts sync on a plan with sync.
 * `?next=/path` returns there afterwards; when this browser was last synced with ANOTHER account the user
 * is sent to Account → Sync to choose merge or replace.
 */

import { useState, type FormEvent } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { PASSWORD_PAUSED, toDigesterError, type AccountSummary } from '@digester/core';
import { showToast } from '../app/Toast.js';
import { useServices } from '../app/services.js';
import { Icon } from '../icons/Icon.js';
import { requestCode, signIn, signInWithPassword, signOut, type SessionState } from '../platform/session.js';
import { passwordSavedMessage, SetPasswordForm } from './Password.js';
import { useAccountState } from './shared.js';

/** Only in-app paths are followed after sign-in. */
export function safeNext(value: string | null, fallback = '/'): string {
  return value && value.startsWith('/') && !value.startsWith('//') ? value : fallback;
}

type Step = 'password' | 'email' | 'code' | 'set-password';

function networkOr(message: string, error: unknown): string {
  const e = toDigesterError(error);
  if (e.code === 'fetch_failed') return 'The server could not be reached. Check your connection and try again.';
  if (e.code === 'rate_limited') return e.message;
  return message;
}

export function SignIn() {
  const { api, session } = useServices();
  const account = useAccountState();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const next = safeNext(params.get('next'));
  const [step, setStep] = useState<Step>('password');
  const [email, setEmail] = useState('');
  const [password, setPasswordValue] = useState('');
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** Password sign-in is paused for this address: offer the code straight away. */
  const [paused, setPaused] = useState(false);
  /** The result of a code sign-in, kept while the password step is shown. */
  const [codeResult, setCodeResult] = useState<SessionState | null>(null);

  const go = (state: SessionState | null): void => {
    if (state?.sync.status === 'conflict') navigate('/settings/account/sync', { replace: true, state: { syncConflict: true } });
    else navigate(next, { replace: true });
  };

  const show = (to: Step): void => {
    setStep(to);
    setError(null);
  };

  const withPassword = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    setPaused(false);
    try {
      const state = await signInWithPassword(session, email, password);
      showToast(`Signed in as ${state.account?.email ?? email}`);
      go(state);
    } catch (e) {
      const err = toDigesterError(e);
      if (err.code === 'rate_limited' && err.details?.reason === PASSWORD_PAUSED) {
        setPaused(true);
        setError(err.message);
      } else if (err.code === 'unauthenticated' || err.code === 'invalid_input') {
        setError('That e-mail address and password do not match. Check them and try again — or use a code instead.');
      } else {
        setError(networkOr(err.message, err));
      }
      setBusy(false);
    }
  };

  const sendCode = async (event?: FormEvent): Promise<void> => {
    event?.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await requestCode(api, email);
      setStep('code');
      setCode('');
    } catch (e) {
      setError(networkOr('The code could not be sent. Try again in a moment.', e));
    } finally {
      setBusy(false);
    }
  };

  const verify = async (event: FormEvent): Promise<void> => {
    event.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const state = await signIn(session, email, code);
      showToast(`Signed in as ${state.account?.email ?? email}`);
      setBusy(false);
      setCodeResult(state);
      setPaused(false);
      setStep('set-password');
    } catch (e) {
      const err = toDigesterError(e);
      setError(err.code === 'unauthenticated' || err.code === 'invalid_input' ? 'That code is not right or has expired. Check it, or send a new one.' : networkOr(err.message, err));
      setBusy(false);
    }
  };

  if (step === 'set-password' && codeResult?.account) {
    const acc: AccountSummary = codeResult.account;
    const fresh = !acc.hasPassword;
    return (
      <div className="account-page">
        <section className="account-card signin-card" aria-labelledby="signin-title">
          <span className="account-card-icon">
            <Icon name="key-round" />
          </span>
          <h1 id="signin-title">{fresh ? 'Set a password' : 'Choose a new password?'}</h1>
          <p>
            {fresh
              ? 'Next time, sign in with your e-mail and this password — no code needed, on any browser or device.'
              : 'You signed in with a code. If you forgot your password, choose a new one now; otherwise keep the one you have.'}
          </p>
          <SetPasswordForm
            email={acc.email}
            needCurrent={false}
            submitLabel={fresh ? 'Set password' : 'Save new password'}
            skipLabel={fresh ? 'Not now' : 'Keep my password'}
            onSkip={() => go(codeResult)}
            note={fresh ? 'You can set or change it later in Account → Profile.' : 'Saving signs you out on your other browsers and apps.'}
            onDone={(res) => {
              showToast(passwordSavedMessage(res, !fresh));
              go(codeResult);
            }}
          />
        </section>
      </div>
    );
  }

  if (account.signedIn) {
    return (
      <div className="account-page">
        <section className="account-card signin-card">
          <span className="account-card-icon">
            <Icon name="user" />
          </span>
          <h1>Signed in</h1>
          <p>
            This browser is signed in as <strong>{account.email}</strong>.
          </p>
          <div className="account-actions">
            <button type="button" className="primary-button" onClick={() => navigate(next)}>
              Continue
            </button>
            <button
              type="button"
              className="secondary-button"
              onClick={async () => {
                await signOut(session);
                showToast('Signed out · this browser keeps its data');
              }}
            >
              <Icon name="log-out" /> Sign out
            </button>
          </div>
        </section>
      </div>
    );
  }

  const errorBox = error ? (
    <p className="form-error" role="alert">
      {error}
    </p>
  ) : null;

  return (
    <div className="account-page">
      <section className="account-card signin-card" aria-labelledby="signin-title">
        <span className="account-card-icon">
          <Icon name={step === 'password' ? 'log-in' : step === 'email' ? 'mail' : 'key-round'} />
        </span>
        <h1 id="signin-title">Sign in</h1>
        {step === 'password' ? (
          <form onSubmit={(e) => void withPassword(e)}>
            <p>Sign in with your e-mail and password.</p>
            <label className="field">
              <span>Email</span>
              <input type="email" name="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            <label className="field">
              <span>Password</span>
              <input type="password" name="password" autoComplete="current-password" required value={password} onChange={(e) => setPasswordValue(e.target.value)} />
            </label>
            {errorBox}
            <div className="account-actions">
              <button type="submit" className="primary-button" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              {paused ? (
                <button type="button" className="secondary-button" disabled={busy || email.trim() === ''} onClick={() => void sendCode()}>
                  E-mail me a code
                </button>
              ) : null}
            </div>
            <div className="signin-alternative">
              <button type="button" className="text-link" onClick={() => show('email')}>
                <Icon name="mail" /> Use a code instead
              </button>
              <span>First time here, no password yet, or forgot it? We e-mail you a six-digit code.</span>
            </div>
          </form>
        ) : step === 'email' ? (
          <form onSubmit={(e) => void sendCode(e)}>
            <p>We e-mail you a six-digit code. Use it the first time, when you have no password yet, or when you forgot it — you can then set a password.</p>
            <label className="field">
              <span>Email</span>
              <input type="email" name="email" autoComplete="username" required autoFocus value={email} onChange={(e) => setEmail(e.target.value)} />
            </label>
            {errorBox}
            <div className="account-actions">
              <button type="submit" className="primary-button" disabled={busy}>
                {busy ? 'Sending…' : 'Send code'}
              </button>
            </div>
            <button type="button" className="text-link" onClick={() => show('password')}>
              <Icon name="arrow-left" /> Sign in with a password
            </button>
          </form>
        ) : (
          <form onSubmit={(e) => void verify(e)}>
            <p>
              Enter the code we sent to <strong>{email}</strong>. It works for a few minutes.
            </p>
            <label className="field">
              <span>Code</span>
              <input
                type="text"
                name="code"
                inputMode="numeric"
                autoComplete="one-time-code"
                pattern="\d{6}"
                maxLength={6}
                required
                autoFocus
                className="code-input"
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
              />
            </label>
            {errorBox}
            <div className="account-actions">
              <button type="submit" className="primary-button" disabled={busy}>
                {busy ? 'Signing in…' : 'Sign in'}
              </button>
              <button type="button" className="secondary-button" disabled={busy} onClick={() => void sendCode()}>
                Send a new code
              </button>
            </div>
            <button type="button" className="text-link" onClick={() => show('email')}>
              <Icon name="arrow-left" /> Use another email
            </button>
          </form>
        )}
        <p className="field-help">Signing in is optional. It adds sync between devices and Pro; everything else works without an account.</p>
        <Link className="text-link account-back" to={next === '/' ? '/' : next}>
          <Icon name="arrow-left" /> Back
        </Link>
      </section>
    </div>
  );
}
